/*
 * LOTR featured pair: variant sync, quick view, sticky bar and add to cart.
 *
 * Cart integration
 * ----------------
 * Each card owns one product form. The card button, the quick-view button and the
 * sticky bar all submit that same form, so they always send the same variant.
 *
 * On submit the form is posted to the locale-aware routes.cart_add_url together with
 * the cart sections the theme wants re-rendered (Shopify's bundled section rendering).
 * The response is handed to the theme's own cart element:
 *
 *   - Dawn-family themes (Dawn, Studio, Sense, Craft, Refresh, ...): <cart-drawer> or
 *     <cart-notification> is detected; getSectionsToRender() supplies the section ids,
 *     publish(PUB_SUB_EVENTS.cartUpdate) notifies subscribers, and renderContents()
 *     refreshes the drawer and cart count and opens it, exactly as Dawn's product-form.js.
 *
 *   - Archetype themes (Motion, Impulse, Streamline, ...) in drawer mode: the theme's own
 *     'ajaxProduct:added' event is dispatched; theme.CartDrawer rebuilds the drawer and the
 *     header count from the live cart and opens it.
 *
 *   - Horizon-family themes (Horizon, Tinker, Atelier, ...): <cart-items-component> section ids
 *     are rendered with the add, a 'cart:update' event carries them to the theme's cart items
 *     and cart icon, and <cart-drawer-component> is opened (unless the theme auto-opens it).
 *
 *   - Any other theme can plug in by defining, before this script runs or at any time:
 *       window.LotrCartAdapter = {
 *         sections() { return ['cart-drawer', 'cart-icon-bubble']; }, // section ids to render
 *         setActiveElement(element) {},                                 // optional, focus return
 *         render(response, variantId, productId, quantity, button) {}   // refresh and open the drawer
 *       };
 *
 *   - With no cart element and no adapter (e.g. "cart page" mode), the shopper is sent
 *     to routes.cart_url after a successful add, mirroring Dawn's behaviour.
 */
if (!customElements.get('lotr-pair')) {
  customElements.define(
    'lotr-pair',
    class LotrPair extends HTMLElement {
      connectedCallback() {
        this.cards = new Map();
        this.timers = new Set();

        this.querySelectorAll('[data-lotr-card]').forEach((el) => {
          const pos = el.dataset.pos;
          const dataEl = this.querySelector(`[data-lotr-data="${pos}"]`);
          const form = document.getElementById(el.dataset.formId);
          if (!dataEl || !form) return;
          let data;
          try {
            data = JSON.parse(dataEl.textContent);
          } catch (error) {
            console.error('lotr-pair: invalid product data', error);
            return;
          }
          const opener = el.querySelector('[data-lotr-quickview]');
          const state = {
            pos,
            el,
            form,
            // Not form.id: the product form contains an input named "id", which shadows it.
            formId: el.dataset.formId,
            data,
            opener,
            dialog: opener ? document.getElementById(opener.dataset.lotrQuickview) : null,
            input: form.querySelector('[data-lotr-variant-input]'),
            variant: null,
            busy: false,
          };
          this.cards.set(pos, state);
          this.watchArtwork(state);
          this.updateCard(state);
        });

        this.sticky = this.querySelector('[data-lotr-sticky]');
        if (this.sticky) {
          this.stickyAdd = this.sticky.querySelector('[data-lotr-sticky-add]');
          this.stickyShortLabel = this.sticky.querySelector('.lotr-sticky__go-short')?.textContent.trim() || '';
          const checked = this.sticky.querySelector('[data-lotr-pick]:checked');
          this.setPick(checked ? checked.value : this.cards.keys().next().value);
        }

        this.onChange = this.onChange.bind(this);
        this.onClick = this.onClick.bind(this);
        this.onSubmit = this.onSubmit.bind(this);
        this.observeSticky = this.observeSticky.bind(this);
        this.addEventListener('change', this.onChange);
        this.addEventListener('click', this.onClick);
        this.addEventListener('submit', this.onSubmit);
        document.addEventListener('shopify:section:load', this.observeSticky);
        document.addEventListener('shopify:section:unload', this.observeSticky);
        this.observeSticky();
      }

      disconnectedCallback() {
        this.io?.disconnect();
        this.footerIo?.disconnect();
        this.timers.forEach(clearTimeout);
        this.removeEventListener('change', this.onChange);
        this.removeEventListener('click', this.onClick);
        this.removeEventListener('submit', this.onSubmit);
        document.removeEventListener('shopify:section:load', this.observeSticky);
        document.removeEventListener('shopify:section:unload', this.observeSticky);
      }

      /* ---------- variant state ---------- */

      resolveVariant(state) {
        const { variants } = state.data;
        const groups = state.form.querySelectorAll('[data-lotr-option]');
        if (!groups.length) {
          return variants.find((v) => String(v.id) === state.input.value) || variants[0] || null;
        }
        const selected = [];
        groups.forEach((group) => {
          const checked = group.querySelector('input:checked');
          if (checked) selected.push([Number(group.dataset.lotrOption), checked.value]);
        });
        return variants.find((v) => selected.every(([index, value]) => v.options[index] === value)) || null;
      }

      strapIndex(state) {
        const checked = state.form.querySelector('[data-lotr-strap] input:checked');
        return checked ? Number(checked.dataset.index) || 0 : 0;
      }

      updateCard(state) {
        const variant = this.resolveVariant(state);
        const available = Boolean(variant && variant.available);
        const { data } = state;
        state.variant = variant;
        if (variant) state.input.value = variant.id;

        const scopes = [state.el, state.dialog].filter(Boolean);
        scopes.forEach((scope) => {
          scope.querySelectorAll('[data-lotr-price]').forEach((node) => {
            if (variant) this.renderMoney(node, variant.price);
          });
        });

        const compare = state.el.querySelector('[data-lotr-compare]');
        if (compare) {
          compare.hidden = !(variant && variant.compare);
          if (variant && variant.compare) compare.textContent = variant.compare;
        }

        const note = state.el.querySelector('[data-lotr-note]');
        if (note) note.textContent = !variant ? data.unavailableLabel : available ? data.priceNote : data.soldOutLabel;

        this.paintValues(state);
        this.controlsFor(state).forEach((button) => this.paintButton(state, button, variant, available));
        this.updateImages(state, variant);
        if (this.picked === state.pos) this.updateSticky();
      }

      // Mark option values that cannot be bought with the other current selections.
      paintValues(state) {
        const groups = [...state.form.querySelectorAll('[data-lotr-option]')];
        const selected = groups.map((group) => [Number(group.dataset.lotrOption), group.querySelector('input:checked')?.value]);
        groups.forEach((group) => {
          const index = Number(group.dataset.lotrOption);
          group.querySelectorAll('input').forEach((input) => {
            const buyable = state.data.variants.some(
              (v) => v.available && v.options[index] === input.value && selected.every(([i, val]) => i === index || v.options[i] === val)
            );
            input.closest('.lotr-strap')?.classList.toggle('is-unavailable', !buyable);
            const status = input.closest('.lotr-strap')?.querySelector('[data-lotr-value-status]');
            if (status) status.textContent = buyable ? '' : `, ${state.data.soldOutLabel}`;
          });
        });
      }

      controlsFor(state) {
        const controls = [...state.el.querySelectorAll('[data-lotr-submit]')];
        if (state.dialog) controls.push(...state.dialog.querySelectorAll('[data-lotr-submit]'));
        if (this.stickyAdd && this.picked === state.pos) controls.push(this.stickyAdd);
        return controls;
      }

      paintButton(state, button, variant, available) {
        const { data } = state;
        button.disabled = !available;
        const text = !variant ? data.unavailableLabel : available ? data.addLabel : data.soldOutLabel;

        if (button === this.stickyAdd) {
          const label = button.querySelector('[data-lotr-sticky-label]');
          if (label) {
            label.replaceChildren();
            if (available) {
              const full = document.createElement('span');
              full.className = 'lotr-sticky__go-full';
              full.textContent = data.addLabel;
              const short = document.createElement('span');
              short.className = 'lotr-sticky__go-short';
              short.textContent = this.stickyShortLabel || data.addLabel;
              label.append(full, short);
            } else {
              label.textContent = text;
            }
          }
          button.setAttribute('aria-label', `${text}: ${data.title}`);
          return;
        }

        if (button.hasAttribute('data-label-add')) {
          const key = !variant ? 'labelUnavailable' : available ? 'labelAdd' : 'labelSoldOut';
          button.setAttribute('aria-label', button.dataset[key]);
          return;
        }

        const label = button.querySelector('[data-lotr-submit-label]');
        if (label) label.textContent = text;
      }

      updateImages(state, variant) {
        const { data } = state;
        const main = state.el.querySelector('.lotr-card__img--main');
        const hover = state.el.querySelector('.lotr-card__img--alt');
        const firstView = state.dialog?.querySelector('[data-lotr-view-main]');
        let image;

        if (data.art && !state.artBroken) {
          const count = data.art.shots.length;
          let index = this.strapIndex(state);
          if (index >= count) index = 0;
          const strap = (data.strapLabels[this.strapIndex(state)] || '').toLowerCase();
          image = { src: data.art.shots[index], srcset: '', alt: strap ? `${data.art.alt}, on the ${strap}` : data.art.alt };
          // A merchant-picked hover image stays put; artwork hovers follow the strap.
          if (hover && !data.customHover && data.art.hovers[index]) hover.src = data.art.hovers[index];
        } else {
          image = (variant && variant.image) || data.fallbackImage;
        }
        if (!image) return;

        if (main && main.tagName === 'IMG') this.setImage(main, image);
        if (firstView) {
          firstView.dataset.src = image.src;
          firstView.dataset.srcset = image.srcset || '';
          const thumb = firstView.querySelector('img');
          if (thumb) {
            thumb.removeAttribute('srcset');
            thumb.src = image.src;
          }
          if (firstView.getAttribute('aria-pressed') === 'true') {
            const qvImg = state.dialog.querySelector('.lotr-qv__img');
            if (qvImg) this.setImage(qvImg, image);
          }
        }
      }

      // Mirrors snippets/lotr-money.liquid: a leading currency symbol gets its own element.
      renderMoney(node, formatted) {
        formatted = String(formatted).replace(/<[^>]*>/g, '').trim();
        const match = /^([^0-9]+)(\d.*)$/.exec(formatted);
        if (!match || !match[1].trim()) {
          node.textContent = formatted;
          return;
        }
        const cur = document.createElement('span');
        cur.className = 'lotr-price__cur';
        cur.textContent = match[1].trim();
        node.replaceChildren(cur, match[2]);
      }

      // Bundled artwork lives in theme assets. If a file is missing (not uploaded),
      // fall back to the product's own media instead of showing a broken image.
      watchArtwork(state) {
        if (!state.data.art) return;
        const main = state.el.querySelector('.lotr-card__img--main');
        if (!main) return;
        const onError = () => this.useProductImages(state);
        main.addEventListener('error', onError, { once: true });
        if (main.complete && main.naturalWidth === 0 && main.getAttribute('src')) onError();
      }

      useProductImages(state) {
        if (state.artBroken) return;
        const { data } = state;
        if (!data.fallbackImage) return;
        state.artBroken = true;

        const hover = state.el.querySelector('.lotr-card__img--alt');
        if (!data.customHover) {
          if (hover && data.hoverImage) this.setImage(hover, data.hoverImage);
          else hover?.remove();
        }

        const views = state.dialog?.querySelector('.lotr-qv__views');
        if (views) {
          const gallery = data.gallery.length > 1 ? data.gallery : [];
          views.replaceChildren(
            ...gallery.map((image, index) => {
              const button = document.createElement('button');
              button.type = 'button';
              button.setAttribute('aria-pressed', String(index === 0));
              button.setAttribute('aria-label', `${views.getAttribute('aria-label')} ${index + 1}`);
              button.dataset.lotrView = '';
              button.dataset.src = image.src;
              button.dataset.srcset = image.srcset || '';
              if (index === 0) button.dataset.lotrViewMain = '';
              const thumb = document.createElement('img');
              thumb.src = image.src;
              thumb.alt = '';
              thumb.width = 46;
              thumb.height = 46;
              thumb.loading = 'lazy';
              button.append(thumb);
              return button;
            })
          );
        }
        const qvImg = state.dialog?.querySelector('.lotr-qv__img');
        if (qvImg) this.setImage(qvImg, (state.variant && state.variant.image) || data.fallbackImage);
        this.updateImages(state, state.variant);
      }

      setImage(img, image) {
        if (image.srcset) img.srcset = image.srcset;
        else img.removeAttribute('srcset');
        img.src = image.src;
        if (image.alt) img.alt = image.alt;
      }

      /* ---------- events ---------- */

      onChange(event) {
        const pick = event.target.closest('[data-lotr-pick]');
        if (pick) {
          this.setPick(pick.value);
          return;
        }
        const state = [...this.cards.values()].find((s) => s.form.contains(event.target));
        if (state) {
          this.clearMessages(state);
          this.updateCard(state);
        }
      }

      onClick(event) {
        const opener = event.target.closest('[data-lotr-quickview]');
        if (opener) {
          const dialog = document.getElementById(opener.dataset.lotrQuickview);
          if (dialog && typeof dialog.showModal === 'function') {
            event.preventDefault();
            dialog.showModal();
          }
          return;
        }

        const close = event.target.closest('[data-lotr-close]');
        if (close) {
          close.closest('dialog')?.close();
          return;
        }

        const view = event.target.closest('[data-lotr-view]');
        if (view) {
          const dialog = view.closest('dialog');
          const img = dialog.querySelector('.lotr-qv__img');
          if (img) {
            this.setImage(img, { src: view.dataset.src, srcset: view.dataset.srcset || '', alt: '' });
            if (view.dataset.width) {
              img.width = Number(view.dataset.width);
              img.height = Number(view.dataset.height);
            }
          }
          dialog.querySelectorAll('[data-lotr-view]').forEach((b) => b.setAttribute('aria-pressed', String(b === view)));
          return;
        }

        // A click on the backdrop lands on the <dialog> element itself.
        if (event.target.matches('dialog[data-lotr-dialog]')) event.target.close();
      }

      /* ---------- sticky bar ---------- */

      setPick(pos) {
        if (!this.cards.has(pos)) return;
        this.picked = pos;
        this.updateSticky();
      }

      updateSticky() {
        const state = this.cards.get(this.picked);
        if (!this.sticky || !state) return;
        const { variant } = state;
        this.stickyAdd.setAttribute('form', state.formId);
        const price = variant ? variant.price : '';
        const priceEl = this.sticky.querySelector('[data-lotr-sticky-price]');
        const priceSm = this.sticky.querySelector('[data-lotr-sticky-price-sm]');
        if (priceEl) priceEl.textContent = price;
        if (priceSm) priceSm.textContent = price ? ` · ${price}` : '';
        this.paintButton(state, this.stickyAdd, variant, Boolean(variant && variant.available));
        this.stickyAdd.classList.toggle('is-loading', state.busy);
      }

      observeSticky() {
        this.io?.disconnect();
        this.footerIo?.disconnect();
        if (!this.sticky) return;
        const targets = [this.querySelector('[data-lotr-grid]'), ...document.querySelectorAll('[data-lotr-sticky-hide]')].filter(Boolean);
        // The site's global footer: hide as soon as any part of it scrolls into view.
        const footers = [
          ...new Set(document.querySelectorAll('.shopify-section-group-footer-group, footer, [role="contentinfo"]')),
        ].filter((el) => !this.contains(el) && !el.parentElement?.closest('.shopify-section-group-footer-group, footer'));
        const seen = new Map([...targets, ...footers].map((t) => [t, targets.includes(t)]));
        const paint = (entries) => {
          entries.forEach((entry) => seen.set(entry.target, entry.isIntersecting));
          const on = ![...seen.values()].some(Boolean);
          this.sticky.classList.toggle('is-on', on);
          this.sticky.inert = !on;
        };
        this.io = new IntersectionObserver(paint, { threshold: 0.15 });
        targets.forEach((t) => this.io.observe(t));
        if (footers.length) {
          this.footerIo = new IntersectionObserver(paint, { threshold: 0 });
          footers.forEach((f) => this.footerIo.observe(f));
        }
      }

      /* ---------- add to cart ---------- */

      getCart() {
        if (window.LotrCartAdapter && typeof window.LotrCartAdapter.render === 'function') return window.LotrCartAdapter;

        // Dawn family (Dawn, Studio, Sense, Craft, Refresh, …).
        const el = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        if (el && typeof el.getSectionsToRender === 'function' && typeof el.renderContents === 'function') {
          return {
            sections: () => el.getSectionsToRender().map((section) => section.id),
            setActiveElement: (node) => el.setActiveElement?.(node),
            render: (response, variantId) => {
              if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
                publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: variantId, cartData: response });
              }
              el.classList.remove('is-empty');
              el.renderContents(response);
            },
          };
        }

        // Archetype themes (Motion, Impulse, Streamline, …) with the cart type set to drawer.
        // theme.CartDrawer listens for 'ajaxProduct:added', rebuilds the drawer and the header
        // count from the live cart, then opens — the same event the theme's own forms dispatch.
        if (this.isArchetypeDrawer()) {
          return {
            sections: () => [],
            render: (response, variantId, productId, quantity, submitter) => {
              document.dispatchEvent(new CustomEvent('ajaxProduct:added', { detail: { product: response, addToCartBtn: submitter } }));
            },
          };
        }

        // Horizon family (Horizon, Tinker, Atelier, Heritage, …): cart items re-render from the
        // 'cart:update' event's bundled sections; the drawer opens itself when auto-open is on.
        const items = [...document.querySelectorAll('cart-items-component[data-section-id]')];
        if (items.length) {
          return {
            sections: () => [...new Set(items.map((item) => item.dataset.sectionId))],
            render: (response, variantId, productId, quantity) => {
              document.dispatchEvent(
                new CustomEvent('cart:update', {
                  bubbles: true,
                  detail: {
                    resource: {},
                    sourceId: String(variantId),
                    data: { source: 'product-form-component', itemCount: quantity, productId: String(productId), sections: response.sections },
                  },
                })
              );
              const drawer = document.querySelector('cart-drawer-component');
              if (drawer && !drawer.hasAttribute('auto-open')) {
                if (typeof drawer.open === 'function') drawer.open();
                else if (typeof drawer.showDialog === 'function') drawer.showDialog();
              }
            },
          };
        }

        return null;
      }

      isArchetypeDrawer() {
        const t = window.theme;
        return Boolean(t && typeof t.CartDrawer === 'function' && document.getElementById('CartDrawer') && (!t.settings || t.settings.cartType === 'drawer'));
      }

      async onSubmit(event) {
        const state = [...this.cards.values()].find((s) => s.form === event.target);
        if (!state) return;
        event.preventDefault();
        if (state.busy) return;

        const { variant } = state;
        if (!variant || !variant.available) return;

        const submitter = event.submitter;
        const fromDialog = Boolean(submitter && state.dialog && state.dialog.contains(submitter));
        const fromSticky = submitter === this.stickyAdd;
        const cart = this.getCart();
        const body = new FormData(state.form);
        body.set('id', variant.id);
        const sections = cart ? cart.sections() : [];
        if (sections.length) {
          body.append('sections', sections);
          body.append('sections_url', window.location.pathname);
        }
        if (cart) {
          // Return focus to a control that is still on the page when the drawer closes.
          cart.setActiveElement?.(fromDialog ? state.opener : submitter || document.activeElement);
        }

        this.setBusy(state, true);
        this.clearMessages(state);

        try {
          const response = await fetch(this.dataset.cartAddUrl, {
            method: 'POST',
            headers: { Accept: 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
            body,
          });
          const json = await response.json();
          if (!response.ok || json.status) {
            throw new Error(json.description || json.message || this.dataset.errorLabel);
          }

          state.el.querySelector('.lotr-add--icon')?.classList.add('is-in');
          if (state.dialog?.open) state.dialog.close();
          this.dispatchEvent(new CustomEvent('lotr:cart:added', { bubbles: true, detail: { variantId: variant.id, response } }));

          if (cart) {
            this.announce(state, this.dataset.addedLabel, false, fromSticky);
            cart.render(json, variant.id, state.data.productId, Number(body.get('quantity')) || 1, fromDialog ? state.opener : submitter);
          } else {
            window.location.href = this.dataset.cartUrl;
          }
        } catch (error) {
          const message = error instanceof TypeError ? this.dataset.errorLabel : error.message;
          this.announce(state, message || this.dataset.errorLabel, true, fromSticky, fromDialog);
        } finally {
          this.setBusy(state, false);
        }
      }

      setBusy(state, busy) {
        state.busy = busy;
        this.controlsFor(state).forEach((button) => {
          button.classList.toggle('is-loading', busy);
          if (busy) button.setAttribute('aria-disabled', 'true');
          else button.removeAttribute('aria-disabled');
        });
      }

      clearMessages(state) {
        [state.el, state.dialog].forEach((scope) => {
          scope?.querySelectorAll('[data-lotr-msg]').forEach((node) => {
            node.textContent = '';
            node.classList.remove('is-error');
          });
        });
        const stickyMsg = this.sticky?.querySelector('[data-lotr-sticky-msg]');
        if (stickyMsg) stickyMsg.textContent = '';
      }

      announce(state, message, isError, fromSticky, fromDialog) {
        const nodes = [...state.el.querySelectorAll('[data-lotr-msg]')];
        if (fromDialog && state.dialog) nodes.push(...state.dialog.querySelectorAll('[data-lotr-msg]'));
        const stickyMsg = this.sticky?.querySelector('[data-lotr-sticky-msg]');
        if (fromSticky && stickyMsg) nodes.push(stickyMsg);
        nodes.forEach((node) => {
          node.textContent = message;
          node.classList.toggle('is-error', isError);
        });
        if (!isError) {
          const timer = setTimeout(() => {
            nodes.forEach((node) => {
              if (node.textContent === message) node.textContent = '';
            });
            this.timers.delete(timer);
          }, 4000);
          this.timers.add(timer);
        }
      }
    }
  );
}
